import { useEffect, useRef } from 'react';

import { SSE } from 'sse.js';

import useGetFetchConfig from '@/api/useGetFetchConfig';

export type ExportPhase = 'processing' | 'uploading';

export interface BaseExportEvent {
  type: string;
  jobId: string;
  jobType: string;
  exportId?: string;
  /** Archive creation progress percentage (0-100) when phase is 'processing' */
  archiveProgress?: number;
  totalDocuments?: number;
  processedDocuments?: number;
  error?: string;
  timestamp?: number;
  /** Phase of the export: 'processing' for document processing, 'uploading' for S3 upload */
  phase?: ExportPhase;
  /** Upload progress percentage (0-100) when phase is 'uploading' */
  uploadProgress?: number;
}

export interface UseExportEventsConfig<TEvent extends BaseExportEvent> {
  progressEndpoint: string;
  enabled?: boolean;
  onEvent: (event: TEvent) => void;
  onError?: () => void;
}

export const useExportEvents = <TEvent extends BaseExportEvent>({
  progressEndpoint,
  onEvent,
  onError,
  enabled = true,
}: UseExportEventsConfig<TEvent>) => {
  const sseRef = useRef<SSE | null>(null);
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  const { fetchConfigGET } = useGetFetchConfig();

  // Keep callback refs up to date without triggering reconnection
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled || !progressEndpoint) {
      return;
    }

    let isMounted = true;

    const connectToSSE = () => {
      try {
        const eventSource = new SSE(progressEndpoint, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: any) => {
          if (!isMounted) return;

          try {
            if (!event.data || !event.data.trim()) {
              return;
            }

            const data: TEvent = JSON.parse(event.data);
            onEventRef.current(data);
          } catch (error) {
            console.error('Failed to parse export event:', error);
          }
        };

        eventSource.onerror = (error: unknown) => {
          console.error('Export SSE error:', JSON.stringify(error));
          if (isMounted) {
            onErrorRef.current?.();
          }
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Failed to setup export event source:', JSON.stringify(error));
      }
    };

    connectToSSE();

    return () => {
      isMounted = false;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [progressEndpoint, enabled, fetchConfigGET.headers]);

  const disconnect = () => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
  };

  return { disconnect };
};
